import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  validatePAN,
  validateGST,
  validateMobile,
  validateEmail,
  validateIFSC,
  validateAccountNumber,
  validateAadhar,
} from "../../../../utils/validationUtils";
import {
  toDateInput,
  validateAgreementStartDate,
  validateAgreementEndDate,
} from "../../../../utils/dateUtils";
import { updateEntityAction } from "../../../../actions/updateEntityAction";
import { useParentOptions } from "../../../../hooks/useParentOptions";
import usePincodeAutofill from "../../../../hooks/usePincodeAutofill";
import { useAadharVisibilityData } from "../../../UploadDetails/AadharVisibilityData";
import { isAadharField as checkIsAadharField } from "../../../../utils/aadharMaskingUtils";
import { validatePANEntityType } from "../../../../utils/panEntityValidation";
import { isPanNumberField } from "../../../../utils/panFieldUtils";
import { normalizeLedgerNameList } from "../../../../utils/ledgerUtils";

// ── Department options — mirrors ClientFields.js AA_DEPT_MAP ──────────────────
const DEPT_BASE = [{ value: "", label: "Select department function" }];
const DEPT_SALES = [...DEPT_BASE, { value: "Sales", label: "Sales" }];
const DEPT_OPS = [
  ...DEPT_BASE,
  { value: "Purchase", label: "Purchase" },
  { value: "Marketing", label: "Marketing" },
  { value: "Operation", label: "Operation" },
];
const DEPT_CORP = [
  ...DEPT_BASE,
  { value: "HR", label: "Human Resources" },
  { value: "IT", label: "Information Technology" },
  { value: "Finance", label: "Finance" },
];
const AA_DEPT_MAP = {
  client: {
    travel: DEPT_SALES,
    auto: DEPT_SALES,
    gadget: DEPT_SALES,
    health: DEPT_SALES,
  },
  "service partner": {
    travel: DEPT_OPS,
    auto: DEPT_OPS,
    gadget: DEPT_OPS,
    travel_service: DEPT_OPS,
    auto_service: DEPT_OPS,
    gadget_service: DEPT_OPS,
    mobile_service: DEPT_OPS,
    corporate: DEPT_CORP,
  },
  corporate: { corporate: DEPT_CORP },
};

const getDeptOptions = (entityCategory, entityLOBType) => {
  const cat = (entityCategory || "").toLowerCase().trim();
  const lob = (entityLOBType || "").toLowerCase().trim();
  return AA_DEPT_MAP[cat]?.[lob] || DEPT_BASE;
};

// ── Field validation ───────────────────────────────────────────────────────────
const validateField = (fieldName, value) => {
  if (!value || (typeof value === "string" && value.trim() === "")) return null;
  switch (fieldName) {
    case "panNumber":
    case "panCardNumber":
    case "CompanypanNumber":
      return validatePAN(value) === true ? null : validatePAN(value);
    case "aadharCardNumber":
    case "aadharNumberOfBothPartners":
    case "directorAadharNumber":
    case "aadharNumberOfTrustee":
      return validateAadhar(value) === true ? null : validateAadhar(value);
    case "gstNumber":
      return validateGST(value) === true ? null : validateGST(value);
    case "mobileNumber":
      return validateMobile(value) === true ? null : validateMobile(value);
    case "emailAddress":
    case "contactEmail":
    case "contactPersonEmail":
      return validateEmail(value) === true ? null : validateEmail(value);
    case "ifscCode":
      return validateIFSC(value) === true ? null : validateIFSC(value);
    case "accountNumber":
      return validateAccountNumber(value) === true
        ? null
        : validateAccountNumber(value);
    default:
      return null;
  }
};

// ── Normalizers ────────────────────────────────────────────────────────────────
const normalizeEntityType = (value) => {
  if (!value) return "";
  const m = {
    proprietorship: "proprietorship",
    partnership: "partnership",
    llp: "llp",
    private_limited: "private_limited",
    public_limited: "public_limited",
    trust: "trust",
    society: "society",
    client: "",
    vendor: "",
  };
  return m[value.toLowerCase().trim()] ?? value;
};

const normalizeLOBType = (value) => {
  if (!value) return "";
  const m = {
    travel: "travel",
    company: "company",
    auto: "auto",
    gadget: "gadget",
    "non-travel": "company",
    nontravel: "company",
  };
  return m[value.toLowerCase().trim()] ?? value;
};

const normalizeLOBNationality = (value) => {
  if (!value) return "";
  const m = { domestic: "Domestic", international: "international" };
  return m[value.toLowerCase().trim()] ?? value;
};

// ── Hook ───────────────────────────────────────────────────────────────────────
export const useEditDetails = (clientData, onSave, onClose) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Parent company options — fetched from API via shared hook
  const { parentOptions } = useParentOptions();

  // Helper: resolve parentName from parentCode using loaded options
  const resolveParentName = (code) => {
    if (!code) return "";
    const match = parentOptions.find((o) => o.value === code);
    // label is "CODE - Name", extract just the name part
    if (match?.label) {
      const parts = match.label.split(" - ");
      return parts.length > 1 ? parts.slice(1).join(" - ") : match.label;
    }
    return "";
  };

  // Department options — reactive to current form values (category + LOB)
  const departmentOptions = getDeptOptions(
    formData.entityCategory || clientData?.entity?.entityCategory,
    formData.entityLOBType ||
      formData.lobType ||
      clientData?.entity?.entityLOBType,
  );

  // Seed form when clientData changes
  useEffect(() => {
    if (!clientData?.entity) return;
    const e = clientData.entity;
    const rawDept = e.aaDepartmentFunction || "";
    // // console.log(
    // //   "[EditModal] dept raw:",
    // //   rawDept,
    // //   "| options:",
    // //   departmentOptions.map((o) => o.value),
    // );

    // KYC fields to blank out when the entity is in draft state.
    // Primary fields (entityName, category, LOB, email, etc.) are always shown.
    const KYC_BLANK_FIELDS = e.isSaveAsDraft
      ? {
          tradeName: "",
          pinCode: "",
          city: "",
          State: "",
          state: "",
          address: "",
          natureOfBusiness: "",
          isInvoiceRequired: "",
          entityType: "",
          // Contact
          contactPersonName: "",
          contactPersonDesignation: "",
          contactPersonMobile: "",
          contactEmail: "",
          // GST
          isGSTRegistered: "",
          gstList: [],
          // Utility
          utilityType: "",
          utilityProviderName: "",
          providerName: "",
          utilityAccountNumber: "",
          accountOrConsumerNumber: "",
          utilityAddress: "",
          addressOnUtilityBill: "",
          // MSME
          isMSMERegistered: "",
          isRegisteredUnderMSME: false,
          msmeNumber: "",
          msmeRegistrationNumber: "",
          // Bank
          hasBankDetails: "",
          bankName: "",
          accountNumber: "",
          ifscCode: "",
          accountType: "",
          branchName: "",
          beneficiaryName: "",
          relationWithBeneficiary: "",
          // Doc numbers
          panCardNumber: "",
          aadharCardNumber: "",
          CompanypanNumber: "",
          aadharNumberOfBothPartners: "",
          aadharNumberOfBothPartnersSecond: "",
          llpCrtificateNumber: "",
          certificateOfIncorporationNumber: "",
          directorPANNumber: "",
          directorAadharNumber: "",
          trustDeedNumber: "",
          panOfTrustNumber: "",
          aadharNumberOfTrustee: "",
          panOfTrusteeNumber: "",
          // Doc paths
          aadharCardPath: "",
          aadharCardBackPath: "",
          panCardPath: "",
          partnershipDeedPath: "",
          uploadLLPCertificatePath: "",
          aadharOfBothPartnersPath: "",
          certificateOfIncorporationPath: "",
          directorPANPath: "",
          directorAadharPath: "",
          msmeCertificatePath: "",
          utilityBillDocumentPath: "",
          passbookOrCancelledChequePath: "",
          uploadSignedFormPath: "",
          trustDeedPath: "",
          panOfTrustPath: "",
          aadharOfTrusteePath: "",
          panOfTrusteePath: "",
        }
      : {};

    //  console.log( "[EditDetailsData] Seeding form with entity:", {
    //     entityId: e.entityId,
    //     hasLedgerList: !!e.ledgerList,
    //     ledgerListLength: e.ledgerList?.length,
    //     ledgerList: e.ledgerList,
    //   });

    // ── folderNo: auto-set GST/bank flags so detail sections render ──────────
    const hasFolderNo = !!e.folderNo;
    const hasGstData = Array.isArray(e.gstList) && e.gstList.length > 0;
    const hasBankData = !!(
      e.bankName ||
      e.accountNumber ||
      e.ifscCode ||
      e.beneficiaryName ||
      e.branchName
    );

    setFormData({
      ...e,
      entityType: normalizeEntityType(e.entityType),
      lobType: normalizeLOBType(e.lobType),
      lobNationality: normalizeLOBNationality(e.lobNationality),
      aaDepartmentFunction: rawDept,
      parentCode: e.parentCode || "",
      parentName: e.parentName || "",
      emailAddress: e.emailAddress || e.contactPersonEmail || "",
      contactPersonEmail: e.emailAddress || e.contactPersonEmail || "",
      agreementStartDate: toDateInput(e.agreementStartDate),
      agreementEndDate: toDateInput(e.agreementEndDate),
      ledgerList: e.ledgerList || [],
      ledgerNameList: normalizeLedgerNameList(e.ledgerNameList),
      // When folderNo exists, force "yes" flags so GST/bank sections show with data
      ...(hasFolderNo && hasGstData ? { isGSTRegistered: "yes" } : {}),
      ...(hasFolderNo && hasBankData ? { hasBankDetails: "yes" } : {}),
      ...KYC_BLANK_FIELDS,
    });
  }, [clientData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── File states for KYC documents ────────────────────────────────────────
  const [utilityFile, setUtilityFile] = useState(null);
  const [msmeFile, setMsmeFile] = useState(null);
  const [thirdPartyFile, setThirdPartyFile] = useState(null);

  // Beneficiary vs trade name match — drives ThirdPartySection visibility
  const showThirdParty =
    formData.hasBankDetails === "yes" &&
    (formData.tradeName || "").trim() !== "" &&
    (formData.beneficiaryName || "").trim() !== "" &&
    (formData.tradeName || "").trim().toLowerCase() !==
      (formData.beneficiaryName || "").trim().toLowerCase();

  // Auto-fill city + state from pincode.
  // skipOnMount=true so the initial API-prefilled city/state are not overwritten.
  usePincodeAutofill({
    pincode: formData.pinCode,
    skipOnMount: true,
    onSuccess: ({ city, state }) => {
      // Field names in this form: "city" (lowercase) and "State" (capital S)
      setFormData((prev) => ({ ...prev, city, State: state, state }));
      setErrors((prev) => {
        const e = { ...prev };
        delete e.pinCode;
        return e;
      });
    },
    onError: (msg) => {
      setFormData((prev) => ({ ...prev, city: "", State: "", state: "" }));
      setErrors((prev) => ({ ...prev, pinCode: msg }));
    },
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (isPanNumberField(name)) return;

    let newValue = type === "checkbox" ? checked : value;

    const uppercaseFields = [
      "panNumber",
      "companyPAN",
      "gstNumber",
      "ifscCode",
    ];
    if (uppercaseFields.includes(name) && typeof newValue === "string")
      newValue = newValue.toUpperCase();

    if (
      ["mobileNumber", "accountNumber", "pinCode"].includes(name) &&
      typeof newValue === "string"
    ) {
      newValue = newValue
        .replace(/\D/g, "")
        .slice(0, name === "pinCode" ? 6 : undefined);
    }

    if (name === "parentCode") {
      // Auto-resolve parentName when parentCode changes
      const resolvedName = resolveParentName(newValue);
      setFormData((prev) => ({
        ...prev,
        parentCode: newValue,
        parentName: resolvedName,
      }));
    } else if (name === "emailAddress") {
      setFormData((prev) => ({
        ...prev,
        emailAddress: newValue,
        contactPersonEmail: newValue,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: newValue }));
    }

    if (type !== "checkbox") {
      const updatedForm = { ...formData, [name]: newValue };
      const error = validateField(name, newValue);
      setErrors((prev) => {
        const e = { ...prev };
        if (error) e[name] = error;
        else delete e[name];
        if (name === "agreementStartDate" || name === "agreementEndDate") {
          const endErr = validateAgreementEndDate(
            updatedForm.agreementEndDate,
            updatedForm.agreementStartDate,
          );
          const startErr = validateAgreementStartDate(
            updatedForm.agreementStartDate,
            updatedForm.agreementEndDate,
          );
          if (endErr) e.agreementEndDate = endErr;
          else delete e.agreementEndDate;
          if (startErr) e.agreementStartDate = startErr;
          else delete e.agreementStartDate;
        }

        // PAN entity type check — runs while typing once PAN is fully valid
        const panFields = ["panCardNumber", "CompanypanNumber", "panNumber"];
        if (panFields.includes(name) && !error) {
          const entityType = updatedForm.entityType || formData.entityType;
          if (entityType) {
            const entityCheck = validatePANEntityType(newValue, entityType);
            // console.log("[PAN Entity Check]", {
            //   pan: newValue,
            //   entityType,
            //   result: entityCheck,
            // });
            if (!entityCheck.isValid) {
              e[name] = entityCheck.message;
              toast.error(`⚠️ PAN Mismatch: ${entityCheck.message}`, {
                position: "top-right",
                autoClose: 6000,
                toastId: "pan-entity-mismatch",
              });
            } else {
              // Clear stale entity-type error
              if (
                e[name] &&
                e[name].includes("does not match selected entity type")
              ) {
                delete e[name];
              }
            }
          }
        }

        return e;
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (isPanNumberField(name)) return;

    const error = validateField(name, value);
    setErrors((prev) => {
      const e = { ...prev };
      if (error) {
        e[name] = error;
        return e;
      }
      delete e[name];

      // Cross-check: PAN and Director PAN must not be the same
      const panFields = ["panCardNumber", "CompanypanNumber", "doc_pan_number"];
      const dirPanFields = ["directorPANNumber", "doc_directorPan_number"];
      const isPan = panFields.includes(name);
      const isDirPan = dirPanFields.includes(name);

      if (isPan || isDirPan) {
        const panVal = (
          isPan
            ? value
            : formData.panCardNumber || formData.CompanypanNumber || ""
        )
          .trim()
          .toUpperCase();
        const dirPanVal = (isDirPan ? value : formData.directorPANNumber || "")
          .trim()
          .toUpperCase();

        if (panVal && dirPanVal && panVal === dirPanVal) {
          if (isPan) e[name] = "PAN Card and Director PAN must not be the same";
          if (isDirPan)
            e[name] = "Director PAN must be different from PAN Card";
        } else {
          // Clear stale cross-check errors
          [...panFields, ...dirPanFields].forEach((f) => {
            if (
              e[f] === "PAN Card and Director PAN must not be the same" ||
              e[f] === "Director PAN must be different from PAN Card"
            ) {
              delete e[f];
            }
          });
        }
      }

      // PAN entity type check — only for main entity PAN fields
      if (isPan && formData.entityType) {
        const entityCheck = validatePANEntityType(value, formData.entityType);
        if (!entityCheck.isValid) {
          e[name] = entityCheck.message;
        }
      }

      return e;
    });
  };

  const handleUpdate = async (
    e,
    uploadedPaths = {},
    localGstEntries = null,
  ) => {
    e.preventDefault();
    const startErr = validateAgreementStartDate(
      formData.agreementStartDate,
      formData.agreementEndDate,
    );
    const endErr = validateAgreementEndDate(
      formData.agreementEndDate,
      formData.agreementStartDate,
    );
    if (startErr || endErr) {
      setErrors((prev) => ({
        ...prev,
        ...(startErr ? { agreementStartDate: startErr } : {}),
        ...(endErr ? { agreementEndDate: endErr } : {}),
      }));
      return;
    }

    // PAN entity type check — block update if mismatch
    const panFields = ["panCardNumber", "CompanypanNumber", "panNumber"];
    for (const field of panFields) {
      const panVal = formData[field];
      if (panVal && formData.entityType) {
        const entityCheck = validatePANEntityType(panVal, formData.entityType);
        if (!entityCheck.isValid) {
          setErrors((prev) => ({ ...prev, [field]: entityCheck.message }));
          toast.error(`⚠️ PAN Mismatch: ${entityCheck.message}`, {
            position: "top-right",
            autoClose: 6000,
            toastId: "pan-entity-mismatch",
          });
          return;
        }
      }
    }

    setIsSubmitting(true);
    try {
      // Merge newly uploaded paths over existing ones — new upload wins
      const mergedData = {
        ...formData,
        ...(uploadedPaths.UtilityBillDocumentPath && {
          utilityBillDocumentPath: uploadedPaths.UtilityBillDocumentPath,
        }),
        ...(uploadedPaths.MSMECertificatePath && {
          msmeCertificatePath: uploadedPaths.MSMECertificatePath,
        }),
        ...(uploadedPaths.UploadSignedFormPath && {
          uploadSignedFormPath: uploadedPaths.UploadSignedFormPath,
        }),
        ...(uploadedPaths.PassbookOrCancelledChequePath && {
          passbookOrCancelledChequePath:
            uploadedPaths.PassbookOrCancelledChequePath,
        }),
        ...(uploadedPaths.AadharCardPath && {
          aadharCardPath: uploadedPaths.AadharCardPath,
        }),
        ...(uploadedPaths.AadharCardBackPath && {
          aadharCardBackPath: uploadedPaths.AadharCardBackPath,
        }),
        ...(uploadedPaths.PANCardPath && {
          panCardPath: uploadedPaths.PANCardPath,
        }),
        ...(uploadedPaths.DirectorPANPath && {
          directorPANPath: uploadedPaths.DirectorPANPath,
        }),
        ...(uploadedPaths.CertificateOfIncorporationPath && {
          certificateOfIncorporationPath:
            uploadedPaths.CertificateOfIncorporationPath,
        }),
        ...(uploadedPaths.PartnershipDeedPath && {
          partnershipDeedPath: uploadedPaths.PartnershipDeedPath,
        }),
        ...(uploadedPaths.UploadLLPCertificatePath && {
          uploadLLPCertificatePath: uploadedPaths.UploadLLPCertificatePath,
        }),
      };
      const result = await updateEntityAction(mergedData.id, {
        ...mergedData,
        // Use the locally-edited GST entries (with isGST/isGSTDef preserved) if provided
        ...(Array.isArray(localGstEntries) && localGstEntries.length > 0
          ? { gstList: localGstEntries }
          : {}),
        isSaveAsDraft: false, // always false on admin update
      });
      if (result.success) {
        toast.success(result.message || "Entity updated successfully!");
        onClose();
      } else {
        toast.error(
          result.message || "Failed to update entity. Please try again.",
        );
      }
    } catch (err) {
      console.error("[handleUpdate] error:", err);
      toast.error(err?.message || "Failed to update entity");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({});
    setErrors({});
    onClose();
  };

  const allDocsUploaded = (() => {
    const docs = clientData?.documents || [];
    if (docs.length === 0) return false;
    return docs.every((doc) => doc.files?.some((f) => f.isUploaded));
  })();

  const getDocumentStatusBadge = (isUploaded) => {
    if (isUploaded) return null;
    // return <span className="doc-status pending">⏳ Pending</span>;
    return null;
  };

  const getFieldValidationState = (fieldName) => {
    const value = formData[fieldName];
    const error = errors[fieldName];
    const hasValue = value && value.toString().trim() !== "";
    const hasValidation = [
      "panNumber",
      "panCardNumber",
      "CompanypanNumber",
      "aadharCardNumber",
      "aadharNumberOfBothPartners",
      "directorAadharNumber",
      "aadharNumberOfTrustee",
      "gstNumber",
      "mobileNumber",
      "emailAddress",
      "contactEmail",
      "contactPersonEmail",
      "ifscCode",
      "accountNumber",
    ].includes(fieldName);
    return {
      error,
      isValid: hasValue && !error && hasValidation,
      className: error
        ? "error"
        : hasValue && !error && hasValidation
          ? "success"
          : "",
    };
  };

  // ── Aadhar Visibility with OTP ────────────────────────────────────────────────
  const entityPrimaryId = clientData?.entity?.entityPrimaryId || 0;
  const aadharVisibility = useAadharVisibilityData(formData, entityPrimaryId);

  // After OTP verify: store decrypted value in originalAadharValues AND update formData
  // so entity[fieldName] shows the real number in the input field
  const handleOtpVerifySuccess = (originalValue) => {
    if (!aadharVisibility.currentAadharField) return;
    const fieldName = aadharVisibility.currentAadharField;

    // Update formData so the input field shows the decrypted value
    if (originalValue) {
      const cleaned = String(originalValue).replace(/\s/g, "");
      setFormData((prev) => ({ ...prev, [fieldName]: cleaned }));
    }

    // Also call the original handler to manage visibility state + auto-hide timer
    aadharVisibility.handleOtpVerifySuccess(originalValue);
  };

  // Helper to get Aadhar display value (masked or full)
  const getAadharDisplayValue = (fieldName) => {
    const rawValue = formData[fieldName] || "";
    if (!rawValue) return "";
    if (!checkIsAadharField(fieldName)) return rawValue;

    return aadharVisibility.getAadharDisplayValue(fieldName, rawValue);
  };

  // console.log("GST NUMNBER. : ",gstList[]);

  return {
    formData,
    setFormData,
    errors,
    isSubmitting,
    departmentOptions,
    parentOptions,
    utilityFile,
    setUtilityFile,
    msmeFile,
    setMsmeFile,
    thirdPartyFile,
    setThirdPartyFile,
    showThirdParty,
    handleChange,
    handleBlur,
    handleUpdate,
    handleClose,
    getDocumentStatusBadge,
    getFieldValidationState,
    allDocsUploaded,
    // Aadhar visibility
    ...aadharVisibility,
    handleOtpVerifySuccess, // override — also updates formData with decrypted value
    getAadharDisplayValue,
    isAadharField: checkIsAadharField,
  };
};
